import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useEffect, useState } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { Icons, ScrollTag } from 'utoniq-ui'

import { MissionType, OrderBy } from '@/clients/utoniq-core/schema'
import { Form, useForm, UseFormProps, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'

export type SearchFormProps = {
  onSubmit: (values: SearchFormValues) => Promise<void>
  sort: OrderBy
}
export type SearchFormValues = {
  typeFilter?: MissionType
  orderBy: OrderBy
}

const schema: UseFormProps['schema'] = o => ({
  typeFilter: o.string().allow('').optional(),
  orderBy: o.string().allow('').optional(),
})

const defaultValues: SearchFormValues = {
  typeFilter: undefined,
  orderBy: OrderBy.Desc,
}

export const SearchForm: Organism<SearchFormProps> = ({ onSubmit, sort }) => {
  const { handleSubmit, control, setValue } = useForm<SearchFormValues>({
    schema,
    defaultValues,
  })

  return (
    <OrganismFrame sx={{ px: 5 }}>
      <Form onSubmit={handleSubmit(onSubmit)} sx={{ bgcolor: 'transparent' }}>
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-6987
         */}
        {/*<ScrollTagCell*/}
        {/*  control={control}*/}
        {/*  setValue={setValue}*/}
        {/*  onChange={handleSubmit(onSubmit)}*/}
        {/*/>*/}
        <FilterCell
          setValue={setValue}
          onChange={handleSubmit(onSubmit)}
          sort={sort}
        />
      </Form>
    </OrganismFrame>
  )
}

type ScrollTagCellProps = {
  control: Control<SearchFormValues>
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
}
const ScrollTagCell: Cell<ScrollTagCellProps> = ({
  control,
  setValue,
  onChange,
}) => {
  const { t } = useTranslation('consoleMissions')
  const type = useWatch({ control, name: 'typeFilter' })

  return (
    <CellFrame>
      <ScrollTag
        value={type}
        tags={[
          { value: undefined, label: t('general') },
          { value: MissionType.Quiz, label: t('answer') },
          { value: MissionType.TwitterRetweet, label: t('retweet') },
          { value: MissionType.TwitterFollow, label: t('follow') },
          { value: MissionType.ItemCollect, label: t('multipleItems') },
        ]}
        onClick={value => {
          setValue('typeFilter', type === value ? undefined : value)
          onChange()
        }}
      />
    </CellFrame>
  )
}

type FilterOptions = {
  value: OrderBy
  label: string
}[]

type FilterCellProps = {
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
  sort: OrderBy
}
const FilterCell: Cell<FilterCellProps> = ({ setValue, onChange, sort }) => {
  const { t } = useTranslation('consoleMissions')
  const filterOptions: FilterOptions = [
    { value: OrderBy.Desc, label: t('newestOrder') },
    { value: OrderBy.Asc, label: t('oldestOrder') },
  ]
  // sort と filterOptions の value を比較して、一致するインデックスを取得
  const initialSelectedIndex = sort
    ? filterOptions.findIndex(option => option.value === sort)
    : 0

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOption, setSelectedOption] = useState<FilterOptions[number]>(
    filterOptions[initialSelectedIndex]
  )
  const open = Boolean(anchorEl)

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = (
    event: MouseEvent<HTMLElement>,
    option: FilterOptions[number]
  ) => {
    setSelectedOption(option)
    setValue('orderBy', option.value)
    onChange()
    setAnchorEl(null)
  }

  useEffect(() => {
    const UrlParams = `?sort=${selectedOption.value}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [selectedOption])

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <CellFrame sx={{ py: 1 }}>
      <Stack direction="row-reverse" spacing={2}>
        <Button variant="text" sx={{ mr: -2 }} onClick={handleClickListItem}>
          <Typography variant="caption" sx={{ mr: 2 }}>
            {selectedOption.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" />
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {filterOptions.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedOption.value}
            onClick={event => handleMenuItemClick(event, option)}
          >
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}
